import { SuperDate, ListFactory } from '@razlet/utils';
import Raid from './raid';
import Shipment from './shipment';
import ShipmentMovement from './shipment/movement';
import {
  createVolumeMask, resolveVolumePrecision, roundVolume,
} from '~/utils/volume';

export default class Booking {
  data = null;

  raid = null;

  createdAt = null;
  disabledAt = null;

  shipment = null;

  allocationCode = null;
  allocationCodeChanging = false;

  mustGo = null;

  priority = null;

  selected = false;

  movements = [];

  constructor (data) {
    this.data = data;

    this.raid = new Raid(data.raid);
    this.createdAt = new SuperDate(data.createdAt);
    if (data.disabledAt) this.disabledAt = new SuperDate(data.disabledAt);

    this.allocationCode = data.allocationCode;
    this.mustGo = !!data.mustGo;
    this.priority = data.priority;

    // eslint-disable-next-line vue/max-len
    if (data.shipment) {
      this.shipment = new Shipment({
        ...data.shipment,
        bookings: [{
          ...data,
          active: true,
          shipment: null,
        }],
      });
    }
    if (data.movements) {
      this.movements = ListFactory.createRows(
        ShipmentMovement,
        data.movements.map(movement => ({
          ...movement,
          shipmentCompany: this.volumeRoundingCompany,
        }))
      );
    }
  }

  get id () {
    return this.data.id;
  }

  get warnings () {
    return this.data.warnings;
  }

  get hasWarnings () {
    if (!this.warnings || !this.warnings.length) return false;
    return this.warnings
      .filter(warning => warning.area !== 'agreement' && warning.level !== 'info').length > 0;
  }

  get cargoPartsCode () {
    return this.data.cargoPartsCode;
  }

  get raid () {
    return this.raid;
  }

  get cargo () {
    if (!this.data?.cargo) return {};
    const volume = this.roundedVolumeValue(this.data.cargo.volume);
    return {
      ...this.data.cargo,
      volume,
      fullName: `${this.data.cargo.pieces}/${this.data.cargo.weight}/${volume}`,
    };
  }

  get volumeRoundingCompany () {
    return this.shipment?.company || this.data?.shipmentCompany ||
      this.raid?.data?.companyCode || this.raid?.data?.iataCompany || null;
  }

  get volumePrecision () {
    return resolveVolumePrecision({ company: this.volumeRoundingCompany });
  }

  get volumeMask () {
    return createVolumeMask(this.volumePrecision);
  }

  roundedVolumeValue (value) {
    return roundVolume(value, this.volumePrecision);
  }

  get createdAt () {
    return this.createdAt;
  }

  get disabledAt () {
    return this.disabledAt;
  }

  get createdBy () {
    return this.data.createdBy;
  }

  get shipment () {
    return this.shipment;
  }

  get notActive () {
    return !this.data.active;
  }

  get isActive () {
    return this.data.active;
  }

  get isStatusEditable () {
    return !['UU', 'CN'].includes(this.allocationCode);
  }

  get isUU () {
    return this.allocationCode === 'UU';
  }

  get shortName () {
    return `${this.raid.company}${this.raid.number}/${this.raid.departure.date.shortDateUpper}`;
  }

  get fullName () {
    // eslint-disable-next-line vue/max-len
    return `${this.raid.company}${this.raid.number}/${this.raid.departure.date.shortDateUpper} ${this.allocationCode}`;
  }

  get shcList () {
    return this.data.shcList;
  }

  get allocatedBy () {
    return this.data.allocatedBy;
  }

  get allocatedReason () {
    return this.data.allocatedReason;
  }

  get reason () {
    return this.data.reason;
  }

  get mawb () {
    return this.shipment?.mawb;
  }

  get cargoParamsInfo () {
    return `${this.cargoPartsCode} ${this.cargo.fullName}`;
  }

  get abilityProps () {
    return {
      company: this.raid.company,
      /* departure: this.shipment.departure,
      arrival: this.shipment.arrival, */
      departure: this.raid.departure.code,
      arrival: this.raid.arrival.code,
    };
  }
};
